import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
  GridActionsCellItem,
  GridRowId,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ToggleOff as InactiveIcon,
  ToggleOn as ActiveIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/store';
import {
  fetchAdminProducts,
  deleteProduct,
  clearError,
} from '../../store/slices/adminSlice';
import { Product } from '../../types';
import { toast } from 'react-toastify';
import { useSettings } from '../../contexts/SettingsContext';
import { formatPrice } from '../../utils/currency';
import { getImageUrl } from '../../utils/imageUtils';

const Products: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { products, loading, error, totalElements, productStatistics } = useSelector((state: RootState) => state.admin);
  const { currencySettings } = useSettings();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  useEffect(() => {
    dispatch(fetchAdminProducts({ page, size: pageSize }) as any);
  }, [dispatch, page, pageSize]);

  // Refresh data when component comes into focus (e.g., returning from edit page)
  useEffect(() => {
    const handleFocus = () => {
      dispatch(fetchAdminProducts({ page, size: pageSize }) as any);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        dispatch(fetchAdminProducts({ page, size: pageSize }) as any);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, page, pageSize]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleEdit = (id: GridRowId) => {
    navigate(`/admin/products/${id}/edit`);
  };

  const handleView = (id: GridRowId) => {
    navigate(`/products/${id}`);
  };

  const handleDeleteClick = (product: Product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (productToDelete) {
      try {
        await dispatch(deleteProduct(productToDelete.id) as any);
        toast.success('Product deleted successfully');
        dispatch(fetchAdminProducts({ page, size: pageSize }) as any);
      } catch (error) {
        toast.error('Failed to delete product');
      }
    }
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const formatPriceWithSettings = (price: number) => {
    return formatPrice(price, currencySettings);
  };

  const columns: GridColDef[] = [
    {
      field: 'imageUrl',
      headerName: 'Image',
      width: 80,
      renderCell: (params) => (
        <Box
          component="img"
          sx={{
            height: 40,
            width: 40,
            borderRadius: 1,
            objectFit: 'cover',
          }}
          src={getImageUrl(params.value)}
          alt={params.row.title}
          onError={(e: any) => {
            e.target.src = '/placeholder-book.svg';
          }}
        />
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: 'title',
      headerName: 'Name',
      width: 250,
      flex: 1,
    },
    {
      field: 'author',
      headerName: 'Author',
      width: 180,
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 150,
      valueGetter: (params) => params.row.category?.name || 'N/A',
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {formatPriceWithSettings(params.value)}
        </Typography>
      ),
    },
    {
      field: 'stock',
      headerName: 'Stock',
      width: 100,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={params.value > 10 ? 'success' : params.value > 0 ? 'warning' : 'error'}
        />
      ),
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          icon={params.value ? <ActiveIcon /> : <InactiveIcon />}
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="View Product">
              <ViewIcon />
            </Tooltip>
          }
          label="View"
          onClick={() => handleView(params.id)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Edit Product">
              <EditIcon />
            </Tooltip>
          }
          label="Edit"
          onClick={() => handleEdit(params.id)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Delete Product">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          onClick={() => handleDeleteClick(params.row)}
          sx={{ color: 'error.main' }}
        />,
      ],
    },
  ];

  const rows: GridRowsProp = (products || []).map((product) => ({
    ...product,
  }));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Product Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/products/new')}
          size="large"
        >
          Add Product
        </Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Products
              </Typography>
              <Typography variant="h4">
                {productStatistics?.total ?? totalElements}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Products
              </Typography>
              <Typography variant="h4" color="success.main">
                {productStatistics?.active ?? products.filter(p => p.active).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Low Stock
              </Typography>
              <Typography variant="h4" color="warning.main">
                {productStatistics?.lowStock ?? products.filter(p => p.stock <= 10 && p.stock > 0).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Out of Stock
              </Typography>
              <Typography variant="h4" color="error.main">
                {productStatistics?.outOfStock ?? products.filter(p => p.stock === 0).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <DataGrid
            rows={rows}
            columns={columns}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={({ page, pageSize }) => {
              setPage(page);
              setPageSize(pageSize);
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            loading={loading}
            disableRowSelectionOnClick
            sx={{
              height: 600,
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
          />
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the product "{productToDelete?.title}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;
